"use client";
import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import {
	AnimatePresence,
	motion,
	useMotionValueEvent,
	useScroll,
} from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

interface NavbarProps {
	children: React.ReactNode;
	className?: string;
}

interface NavBodyProps {
	children: React.ReactNode;
	className?: string;
	visible?: boolean;
}

interface NavItemsProps {
	items: {
		name: string;
		link: string;
	}[];
	className?: string;
	onItemClick?: () => void;
}

interface MobileNavProps {
	children: React.ReactNode;
	className?: string;
	visible?: boolean;
}

interface MobileNavHeaderProps {
	children: React.ReactNode;
	className?: string;
}

interface MobileNavMenuProps {
	children: React.ReactNode;
	className?: string;
	isOpen: boolean;
	onClose: () => void;
}

export const Navbar = ({ children, className }: NavbarProps) => {
	// Track window scroll, not the fixed navbar element
	const { scrollY } = useScroll();
	const [visible, setVisible] = useState<boolean>(false);

	useMotionValueEvent(scrollY, "change", (latest) => {
		if (latest > 100) {
			setVisible(true);
		} else {
			setVisible(false);
		}
	});

	return (
		<motion.div
			className={cn("fixed inset-x-0 top-0 z-[60] w-full", className)}
		>
			{React.Children.map(children, (child) =>
				React.isValidElement(child)
					? React.cloneElement(
							child as React.ReactElement<{ visible?: boolean }>,
							{ visible },
						)
					: child,
			)}
		</motion.div>
	);
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
	return (
		<motion.div
			animate={{
				backdropFilter: visible ? "blur(12px)" : "blur(6px)",
				boxShadow: visible
					? "0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"
					: "0 0 0 1px rgba(255, 255, 255, 0.03) inset",
				y: visible ? 16 : 0,
			}}
			transition={{
				type: "spring",
				stiffness: 200,
				damping: 30,
			}}
			style={{
				// Use inline style for width animation to avoid Tailwind class conflicts
				width: visible ? "fit-content" : "100%",
			}}
			className={cn(
				// Base layout - Flexbox with space-between
				"relative z-[60] mx-auto max-w-7xl flex-row items-center justify-between gap-6 px-6 py-3",
				// Rounded when shrunk
				visible ? "rounded-full" : "rounded-none",
				// Responsive: hidden on mobile, flex on desktop
				"hidden lg:flex",
				// Background
				"bg-background/90 border border-border/30 shadow-lg backdrop-blur-xl",
				visible && "bg-background/95 border-border/40 shadow-xl",
				className,
			)}
		>
			{children}
		</motion.div>
	);
};

export const NavItems = ({ items, className, onItemClick }: NavItemsProps) => {
	const [hovered, setHovered] = useState<number | null>(null);

	// Clear hover state when mouse leaves all nav items
	// Using a timeout to allow moving between items without flicker
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleMouseEnter = (idx: number) => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
		setHovered(idx);
	};

	const handleMouseLeave = () => {
		hoverTimeoutRef.current = setTimeout(() => {
			setHovered(null);
		}, 100);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	return (
		<div
			className={cn(
				// Flexbox centering - positioned in center of parent
				"flex items-center justify-center gap-1 text-sm text-muted-foreground",
				// Responsive: hidden on mobile, flex on desktop
				"hidden lg:flex",
				// Ensure it's in the center between logo and right side
				"mx-auto",
				className,
			)}
		>
			{items.map((item, idx) => (
				<Link
					activeProps={{
						className: "text-foreground font-semibold",
						"aria-current": "page",
					}}
					className="relative px-4 py-2 text-muted-foreground transition-colors hover:text-foreground"
					key={item.link}
					onClick={onItemClick}
					onMouseEnter={() => handleMouseEnter(idx)}
					onMouseLeave={handleMouseLeave}
					to={item.link}
				>
					{({ isActive }: { isActive: boolean }) => (
						<>
							{(hovered === idx || isActive) && (
								<motion.div
									className="pointer-events-none absolute inset-0 h-full w-full rounded-full bg-muted"
									layoutId="hovered"
								/>
							)}
							<span className="relative z-20">{item.name}</span>
						</>
					)}
				</Link>
			))}
		</div>
	);
};

export const MobileNav = ({ children, className, visible }: MobileNavProps) => {
	return (
		<motion.div
			animate={{
				backdropFilter: visible ? "blur(12px)" : "blur(8px)",
				boxShadow: visible ? "0 4px 30px rgba(0, 0, 0, 0.1)" : "none",
			}}
			transition={{
				type: "spring",
				stiffness: 300,
				damping: 30,
			}}
			className={cn(
				"relative z-50 mx-auto w-full flex-col items-center justify-between px-4 py-3",
				// Responsive: flex on mobile, hidden on desktop
				"flex lg:hidden",
				// Background
				"bg-background/90 border-b border-border/30",
				visible && "bg-background/95 border-border/50 shadow-sm",
				className,
			)}
		>
			{children}
		</motion.div>
	);
};

export const MobileNavHeader = ({
	children,
	className,
}: MobileNavHeaderProps) => {
	return (
		<div
			className={cn(
				"flex w-full flex-row items-center justify-between",
				className,
			)}
		>
			{children}
		</div>
	);
};

export const MobileNavMenu = ({
	children,
	className,
	isOpen,
	onClose,
}: MobileNavMenuProps) => {
	const menuRef = useRef<HTMLDivElement>(null);

	// Handle Escape key to close menu (WCAG 2.1.2 Keyboard)
	useEffect(() => {
		if (!isOpen) return;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isOpen, onClose]);

	// Handle click outside to close menu
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				onClose();
			}
		};

		// Delay to avoid closing immediately on open
		const timer = setTimeout(() => {
			document.addEventListener("click", handleClickOutside);
		}, 100);

		return () => {
			clearTimeout(timer);
			document.removeEventListener("click", handleClickOutside);
		};
	}, [isOpen, onClose]);

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					ref={menuRef}
					animate={{ opacity: 1 }}
					aria-modal="true"
					className={cn(
						"absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg border border-border bg-popover px-4 py-8 shadow-lg",
						className,
					)}
					exit={{ opacity: 0 }}
					initial={{ opacity: 0 }}
					role="dialog"
				>
					{children}
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export const MobileNavToggle = ({
	isOpen,
	onClick,
}: {
	isOpen: boolean;
	onClick: () => void;
}) => (
	<button
		aria-expanded={isOpen}
		aria-label={isOpen ? "Close menu" : "Open menu"}
		className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		onClick={onClick}
		type="button"
	>
		{isOpen ? (
			<X className="h-6 w-6 text-foreground" />
		) : (
			<Menu className="h-6 w-6 text-foreground" />
		)}
	</button>
);

export const NavbarLogo = () => (
	<Link
		className="relative z-20 flex items-center py-1 font-brand text-foreground text-lg transition-colors hover:text-primary"
		to="/"
	>
		<span className="whitespace-nowrap font-semibold tracking-wide">
			DESIGNS{" "}
			<span className="font-normal italic text-muted-foreground">by</span>{" "}
			MARYAM
		</span>
	</Link>
);

export const NavbarButton = ({
	href,
	as: Tag = "a",
	children,
	className,
	variant = "primary",
	...props
}: {
	href?: string;
	as?: React.ElementType;
	children: React.ReactNode;
	className?: string;
	variant?: "primary" | "secondary" | "dark" | "gradient";
} & (
	| React.ComponentPropsWithoutRef<"a">
	| React.ComponentPropsWithoutRef<"button">
)) => {
	const baseStyles =
		"inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-5 py-2.5 bg-card text-card-foreground text-sm font-semibold tracking-[0.02em] relative cursor-pointer transition duration-200 text-center ring-1 ring-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60";

	const variantStyles = {
		primary:
			"shadow-md border border-border hover:-translate-y-0.5 hover:shadow-lg",
		secondary:
			"bg-transparent shadow-none text-foreground hover:bg-foreground/5 ring-border/60",
		dark: "bg-foreground text-background shadow-md hover:-translate-y-0.5",
		gradient:
			"bg-gradient-to-br from-gradient-gold-from via-gradient-gold-via to-gradient-gold-to text-slate-900 shadow-gold-glow ring-white/50 hover:-translate-y-0.5 hover:shadow-gold-glow",
	};

	return (
		<Tag
			className={cn(baseStyles, variantStyles[variant], className)}
			href={href || undefined}
			{...props}
		>
			{children}
		</Tag>
	);
};
